import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useTherapyModalities, parseOrganizationUrls, pickLang } from '@/hooks/useTherapyModalities';
import { useTherapyModalityStats } from '@/hooks/useTherapyModalityStats';
import { useSubcategoryLabels, localizeSubcategories } from '@/hooks/useSubcategoryLabels';
import { useWikiAccessBeacon } from '@/hooks/useWikiAccessBeacon';
import { useTeenMode } from '@/hooks/useTeenMode';

import { Loader2, ArrowLeft, ArrowUpRight, ExternalLink, ChevronsDownUp, ChevronsUpDown, RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import EditorialMarkdown from '@/components/therapy/EditorialMarkdown';
import {
  localizeContraindicationFlags,
  localizeFormat,
  localizeFrequency,
  localizeDomainFlags,
  hasDomainFlags,
  localizeTherapyType,
  localizeAgeGroups,
  formatAgeRange,
} from '@/utils/therapy-vocab';

interface Section {
  id: string;
  title: string;
}

const TherapyKnowledgePage: React.FC = () => {
  const { therapyId } = useParams<{ therapyId: string }>();
  const { t, i18n } = useTranslation('therapyLibrary');
  const isZh = i18n.language === 'zh';

  const { teenMode } = useTeenMode();
  const { data: therapies, isLoading } = useTherapyModalities();

  const { data: stats } = useTherapyModalityStats();
  const { data: subcategoryMap } = useSubcategoryLabels();

  const therapy = useMemo(
    () => therapies?.find((x) => x.id === therapyId) || null,
    [therapies, therapyId]
  );

  // Provenance beacon: coarse trace of which entry this reader was served.
  const servedIds = useMemo(() => (therapy ? [therapy.id] : []), [therapy]);
  useWikiAccessBeacon('therapy_wiki', servedIds);

  const name = therapy ? pickLang(isZh, therapy.name_cn, therapy.name_en) : '';
  const mechanism = therapy ? pickLang(isZh, therapy.mechanism_cn, therapy.mechanism_en) : '';
  const procedures = therapy
    ? pickLang(isZh, therapy.procedures_cn, therapy.procedures_en)
    : '';
  // Narrative fields: unsuffixed columns hold Chinese, *_en columns hold English
  const origin = therapy ? pickLang(isZh, therapy.origin_narrative, therapy.origin_narrative_en) : '';
  const population = therapy ? pickLang(isZh, therapy.population_prose, therapy.population_prose_en) : '';
  const contraindications = therapy
    ? pickLang(isZh, therapy.contraindication_prose, therapy.contraindication_prose_en)
    : '';
  const ageRangeLabel = therapy ? formatAgeRange(isZh, therapy.min_age, therapy.max_age) : null;
  const ageGroupLabels = therapy ? localizeAgeGroups(isZh, therapy.age_groups) : [];
  const ageNotes = therapy ? pickLang(isZh, therapy.age_notes, therapy.age_notes_en) : '';

  // Teaching layer — core claim, concept glossary, first session, misconceptions
  const keyClaim = therapy ? pickLang(isZh, therapy.key_claim_cn, therapy.key_claim_en) : '';
  const firstSession = therapy ? pickLang(isZh, therapy.first_session_prose, therapy.first_session_en) : '';
  const coreConcepts = useMemo(() => {
    const raw = (isZh ? therapy?.core_concepts : therapy?.core_concepts_en) || therapy?.core_concepts_en || therapy?.core_concepts;
    if (!Array.isArray(raw)) return [];
    return (raw as unknown[])
      .map((c) => (c && typeof c === 'object' ? (c as Record<string, unknown>) : null))
      .filter((c): c is Record<string, unknown> => !!c)
      .map((c) => ({
        term: typeof c.term === 'string' ? c.term : '',
        termEn: typeof c.term_en === 'string' ? c.term_en : '',
        oneLiner: typeof c.one_liner === 'string' ? c.one_liner : '',
        whyItMatters: typeof c.why_it_matters === 'string' ? c.why_it_matters : '',
      }))
      .filter((c) => c.term || c.termEn);
  }, [therapy, isZh]);
  const misconceptions = useMemo(() => {
    const raw = (isZh ? therapy?.misconceptions : therapy?.misconceptions_en) || therapy?.misconceptions_en || therapy?.misconceptions;
    if (!Array.isArray(raw)) return [];
    return (raw as unknown[])
      .map((m) => (m && typeof m === 'object' ? (m as Record<string, unknown>) : null))
      .filter((m): m is Record<string, unknown> => !!m)
      .map((m) => ({
        myth: typeof m.myth === 'string' ? m.myth : '',
        reality: typeof m.reality === 'string' ? m.reality : '',
      }))
      .filter((m) => m.myth && m.reality);
  }, [therapy, isZh]);






  const guidance = therapy
    ? pickLang(isZh, therapy.practitioner_guidance_prose, therapy.practitioner_guidance_prose_en)
    : '';
  const streamTypeLabel = therapy?.stream_type
    ? t(`streamTypes.${therapy.stream_type}`, {
        defaultValue: (isZh && therapy.stream_type_cn) || therapy.stream_type,
      })
    : null;
  const targetDomainLabel = therapy ? localizeDomainFlags(isZh, therapy) : null;
  const typeLabel = therapy ? localizeTherapyType(isZh, therapy.type, therapy.type_cn) : null;
  const subcategoryLabels = localizeSubcategories(isZh, therapy?.subcategories, subcategoryMap);
  const parentCategoryLabel = subcategoryLabels.length ? subcategoryLabels.join(' · ') : null;
  const formatLabel = therapy ? localizeFormat(isZh, therapy.format, therapy.format_en) : null;
  const frequencyLabel = therapy ? localizeFrequency(isZh, therapy.frequency_per_week) : null;
  const cautionFlags = localizeContraindicationFlags(isZh, therapy?.contraindication_flags);
  const certifications = therapy?.certifications?.length ? therapy.certifications.join('\n') : '';
  const organizations = therapy?.organizations?.length ? therapy.organizations.join('\n') : '';

  const range = (min?: number | null, max?: number | null, single?: number | null) => {
    if (min && max) return min === max ? `${min}` : `${min}–${max}`;
    return (min || max || single) ? String(min || max || single) : null;
  };
  const sessionLength = therapy
    ? range(therapy.session_length_min, therapy.session_length_max)
    : null;
  const sessionCount = therapy
    ? range(therapy.recommended_sessions_min, therapy.recommended_sessions_max)
    : null;
  const lineageGroups = therapy
    ? [
        { key: 'derivedFrom', fallback: 'Derived from', items: therapy.derived_from },
        { key: 'developedInto', fallback: 'Developed into', items: therapy.developed_into },
        { key: 'synergisticWith', fallback: 'Works well with', items: therapy.synergistic_with },
        { key: 'contrastsWith', fallback: 'Contrasts with', items: therapy.contrasts_with },
      ].filter((g) => g.items && g.items.length > 0)
    : [];
  const hasStructure = !!(sessionLength || sessionCount || therapy?.frequency_per_week || therapy?.format ||
    therapy?.cost_per_session_min || therapy?.cost_per_session_max || therapy?.chata_stage);

  const therapyCanonical = therapy
    ? `https://resonow.org/framework/${therapyId}`
    : 'https://resonow.org/framework';
  const therapyDescription = (therapy?.mechanism_en || t('pageSubtitle') || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 155);

  useSEO({
    title: therapy ? `${String(therapy.name_en).slice(0, 40)} — Therapy Guide | Resonow` : t('pageTitle'),
    description: therapyDescription,
    canonical: therapyCanonical,
    openGraph: {
      title: therapy ? `${therapy.name_en} — Therapy Guide` : t('pageTitle'),
      description: therapyDescription,
      type: 'article',
      url: therapyCanonical,
    },
    structuredData: therapy
      ? ({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${String(therapy.name_en).slice(0, 100)} — Therapy Guide`,
          description: therapyDescription,
          url: therapyCanonical,
          mainEntityOfPage: therapyCanonical,
          datePublished: (therapy as { created_at?: string }).created_at || undefined,
          dateModified: (therapy as { updated_at?: string }).updated_at || undefined,
          about: { '@type': 'MedicalTherapy', name: therapy.name_en },
          author: { '@type': 'Organization', name: 'Resonow', url: 'https://resonow.org' },
          publisher: {
            '@type': 'Organization',
            name: 'Resonow',
            url: 'https://resonow.org',
            logo: { '@type': 'ImageObject', url: 'https://resonow.org/logo.png' },
          },
        } as unknown as any[])
      : undefined,
  });

  // Collapse-all state for the long-form body
  const [collapsed, setCollapsed] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  // Reset scroll on mount
  useEffect(() => { window.scrollTo(0, 0); setCollapsed(false); }, [therapyId]);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (next && headerRef.current) {
        // Scroll back to overview so the user keeps context
        requestAnimationFrame(() => {
          headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      return next;
    });
  };

  // Related modalities — curated lineage relations first, same-stream entries as fallback.
  const related = useMemo(() => {
    if (!therapy || !therapies) return [];
    const norm = (s: string) => s.trim().toLowerCase();
    const byName = new Map<string, typeof therapies[number]>();
    therapies.forEach((x) => {
      if (x.name_en) byName.set(norm(x.name_en), x);
      if (x.name_cn) byName.set(norm(x.name_cn), x);
    });

    const curatedGroups: Array<{ names: string[] | null; key: string; fallback: string }> = [
      { names: therapy.derived_from, key: 'derivedFrom', fallback: 'Derived from' },
      { names: therapy.developed_into, key: 'developedInto', fallback: 'Developed into' },
      { names: therapy.synergistic_with, key: 'synergisticWith', fallback: 'Works well with' },
      { names: therapy.contrasts_with, key: 'contrastsWith', fallback: 'Contrasts with' },
    ];

    const seen = new Set<string>();
    const curated: Array<{ id: string; linkId: string; name: string; relation: string; difference: string }> = [];
    curatedGroups.forEach((group) => {
      (group.names ?? []).forEach((raw) => {
        const match = byName.get(norm(String(raw ?? '')));
        if (!match || match.id === therapy.id || seen.has(match.id)) return;
        seen.add(match.id);
        curated.push({
          id: match.id,
          linkId: match.id,
          name: pickLang(isZh, match.name_cn, match.name_en) || '',
          relation: group.key,
          difference: '',
        });
      });
    });
    if (curated.length > 0) return curated.slice(0, 8);

    return therapies
      .filter((x) => x.id !== therapy.id && x.stream_type && x.stream_type === therapy.stream_type)
      .slice(0, 6)
      .map((x) => ({
        id: x.id,
        linkId: x.id,
        name: pickLang(isZh, x.name_cn, x.name_en) || '',
        relation: '',
        difference: '',
      }));
  }, [therapy, therapies, isZh, t]);



  // Active stages for this therapy
  const relatedStages = useMemo(() => {
    if (!therapy) return [];
    const list: number[] = [];
    for (let i = 0; i <= 4; i++) {
      if ((therapy as any)[`stage_${i}`] === true) list.push(i);
    }
    return list;
  }, [therapy]);

  const orgUrls = therapy ? parseOrganizationUrls(therapy.organization_urls) : [];

  const sections: Section[] = useMemo(() => {
    const s: Section[] = [{ id: 'overview', title: t('know.overview', { defaultValue: 'Overview' }) }];
    if (mechanism) s.push({ id: 'mechanism', title: t('know.mechanism', { defaultValue: 'Mechanism' }) });
    if (procedures) s.push({ id: 'procedures', title: t('know.procedures', { defaultValue: 'How it works' }) });
    if (coreConcepts.length) s.push({ id: 'concepts', title: t('know.concepts', { defaultValue: 'Key concepts' }) });
    if (firstSession) s.push({ id: 'firstSession', title: t('know.firstSession', { defaultValue: 'Your first session' }) });
    if (misconceptions.length) s.push({ id: 'misconceptions', title: t('know.misconceptions', { defaultValue: 'Common misconceptions' }) });
    if (origin) s.push({ id: 'origin', title: t('know.origin', { defaultValue: 'Origin' }) });
    if (population || (therapy && hasDomainFlags(therapy)) || relatedStages.length || ageGroupLabels.length) s.push({ id: 'helps', title: t('know.helps', { defaultValue: 'Who it helps' }) });
    if (contraindications || cautionFlags.length > 0) s.push({ id: 'cautions', title: t('know.cautions', { defaultValue: 'Cautions & contraindications' }) });

    if (hasStructure) s.push({ id: 'structure', title: t('know.structure', { defaultValue: 'Session structure' }) });
    if (guidance) s.push({ id: 'guidance', title: t('know.guidance', { defaultValue: 'Practitioner guidance' }) });
    if (certifications || organizations || lineageGroups.length) s.push({ id: 'lineage', title: t('know.lineage', { defaultValue: 'Lineage & certifications' }) });
    if (related.length) s.push({ id: 'related', title: t('know.related', { defaultValue: 'Related modalities' }) });
    s.push({ id: 'references', title: t('know.furtherReading', { defaultValue: 'Further reading' }) });
    s.push({ id: 'practitioners', title: t('know.practitioners', { defaultValue: 'Find a practitioner' }) });
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, mechanism, procedures, origin, population, contraindications, guidance, hasStructure, certifications, organizations, therapy, relatedStages, related, lineageGroups.length, ageGroupLabels.length, coreConcepts.length, firstSession, misconceptions.length]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-amber-50/50">
        <Navbar />
        <main className="py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-3xl space-y-4" aria-busy="true" aria-live="polite">
            <span className="sr-only">{t('know.loading', { defaultValue: 'Loading entry…' })}</span>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-5 w-1/2" />
            <div className="pt-6 space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
            </div>
            <div className="pt-6 space-y-3">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!therapy) {
    return (
      <div className="min-h-screen bg-amber-50/50">
        <Navbar />
        <main className="py-24 container mx-auto px-4 max-w-3xl text-center">
          <h1 className="text-3xl font-serif text-earth-900 mb-3">{t('know.notFound', { defaultValue: 'Entry not found' })}</h1>
          <p className="text-earth-700 mb-6">
            {t('know.notFoundHint', { defaultValue: "We couldn't load this entry. It may have been moved, or the connection dropped." })}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="w-4 h-4 mr-2" /> {t('know.retry', { defaultValue: 'Try again' })}
            </Button>
            <Link to="/framework" className="text-amber-700 hover:underline inline-flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('know.backToArchive', { defaultValue: 'Back to archive' })}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (teenMode && therapy.teen_status === 'excluded') {
    const reason = pickLang(isZh, therapy.teen_exclusion_reason_cn, therapy.teen_exclusion_reason_en);
    return (
      <div className="min-h-screen bg-amber-50/50">
        <Navbar />
        <main className="py-24 container mx-auto px-4 max-w-3xl text-center">
          <h1 className="text-3xl font-serif text-earth-900 mb-3">
            {t('teen.blockedTitle', { defaultValue: 'Not available in teen mode' })}
          </h1>
          <p className="text-earth-700 mb-6">
            {reason ||
              t('teen.blockedBody', {
                defaultValue: 'This entry is outside the 14+ content set.',
              })}
          </p>
          <Link to="/framework" className="text-amber-700 hover:underline inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('know.backToArchive', { defaultValue: 'Back to archive' })}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const practitionerCount = stats?.find((s) => s.therapy_modality_id === therapy.id)?.practitioner_count ?? 0;

  const evidenceLabel = therapy.evidence_level
    ? t(`evidenceLevels.${therapy.evidence_level}`, { defaultValue: `Lvl. ${therapy.evidence_level}` })
    : null;

  // Curated citations, when the entry has them in the database
  const curatedReferences = Array.isArray(therapy.references_json)
    ? (therapy.references_json as unknown[])
        .map((r) => (r && typeof r === 'object' ? (r as Record<string, unknown>) : null))
        .filter((r): r is Record<string, unknown> => !!r && typeof r.title === 'string')
        .map((r) => ({ title: String(r.title), url: typeof r.url === 'string' ? r.url : null }))
    : [];

  // Maturity signals for the entry
  const teachingComplete = !!keyClaim && coreConcepts.length > 0 && !!firstSession && misconceptions.length > 0;
  const referencesVerified = curatedReferences.filter((r) => !!r.url).length >= 3;

  // Generated search entry points — NOT curated citations

  const encName = encodeURIComponent(therapy.name_en);
  const searchLinks = [
    { label: 'Wikipedia', url: `https://en.wikipedia.org/wiki/Special:Search?search=${encName}` },
    { label: 'Google Scholar', url: `https://scholar.google.com/scholar?q=${encName}` },
    { label: 'PubMed', url: `https://pubmed.ncbi.nlm.nih.gov/?term=${encName}` },
  ];

  return (
    <div className="min-h-screen bg-amber-50/50">
      <Navbar />

      <main className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Breadcrumb + Collapse toggle */}
          <div className="mb-10 flex items-center justify-between gap-4">
            <nav className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans">
              <Link to="/framework" className="hover:text-[hsl(var(--primary))] inline-flex items-center">
                <ArrowLeft className="w-3 h-3 mr-2" /> {t('know.archive', { defaultValue: 'Healing Archive' })}
              </Link>
              <span className="mx-3 text-stone-300">/</span>
              <span className="text-stone-700 normal-case tracking-normal font-serif italic text-sm">{name}</span>
            </nav>
            <button
              type="button"
              onClick={toggleCollapse}
              aria-expanded={!collapsed}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-sm border border-[hsl(var(--primary)/0.4)] bg-amber-50/60 text-stone-700 hover:bg-[hsl(var(--primary)/0.1)] hover:text-stone-900 hover:border-[hsl(var(--primary))] transition-colors text-[11px] font-sans uppercase tracking-[0.2em]"
            >
              {collapsed ? (
                <>
                  <ChevronsUpDown className="w-3.5 h-3.5" />
                  {t('know.expandAll', { defaultValue: 'Expand all' })}
                </>
              ) : (
                <>
                  <ChevronsDownUp className="w-3.5 h-3.5" />
                  {t('know.collapseAll', { defaultValue: 'Collapse all' })}
                </>
              )}
            </button>
          </div>


          <div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)_240px] gap-12 lg:gap-16">
            {/* Left TOC */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 font-sans mb-4">
                  {t('know.contents', { defaultValue: 'Contents' })}
                </p>
                <ul className="space-y-2.5 text-sm font-sans">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`} className="text-stone-600 hover:text-[hsl(var(--primary))] transition-colors">
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Center article */}
            <article className="min-w-0 max-w-2xl mx-auto lg:mx-0">
              {/* Header */}
              <header id="overview" ref={headerRef} className="mb-14 scroll-mt-24">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-6">
                  {streamTypeLabel && (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-sans">{streamTypeLabel}</span>
                  )}
                  {evidenceLabel && (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans">{evidenceLabel}</span>
                  )}
                </div>
                <h1 className="font-serif italic text-4xl md:text-6xl text-stone-900 leading-[1.05] tracking-tight mb-4">
                  {name}
                </h1>
                {teenMode && therapy.teen_status === 'restricted' && (
                  <p className="mb-6 rounded-sm border border-[hsl(var(--primary)/0.35)] bg-[hsl(var(--primary)/0.07)] px-3 py-2 font-sans text-xs leading-relaxed text-stone-700">
                    {pickLang(isZh, therapy.teen_exclusion_reason_cn, therapy.teen_exclusion_reason_en) ||
                      t('teen.guardianNotice', {
                        defaultValue: 'Guardian awareness and presence required for minors.',
                      })}
                  </p>
                )}

                {therapy.abbreviation && (
                  <p className="text-stone-400 font-sans text-xs tracking-[0.25em] uppercase mb-8">
                    {therapy.abbreviation}
                  </p>
                )}
                {mechanism && (
                  <blockquote className="border-l-2 border-[hsl(var(--primary)/0.5)] pl-6 my-8">
                    <p className="font-serif italic text-xl md:text-2xl text-stone-700 leading-relaxed">
                      “{mechanism}”
                    </p>
                  </blockquote>
                )}
                {keyClaim && (
                  <div className="mt-8 border border-[hsl(var(--primary)/0.3)] bg-amber-50/70 rounded-sm px-6 py-5">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                      {t('know.keyClaim', { defaultValue: 'In one sentence' })}
                    </p>
                    <p className="font-serif text-lg md:text-xl text-stone-800 leading-relaxed">{keyClaim}</p>
                  </div>
                )}
                {(teachingComplete || referencesVerified) && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {teachingComplete && (
                      <span className="inline-flex items-center rounded-full border border-[hsl(var(--primary)/0.35)] bg-amber-50/70 px-3 py-1 text-[11px] font-sans tracking-wide text-stone-600">
                        {t('know.teachingComplete', { defaultValue: 'Teaching layer complete' })}
                      </span>
                    )}
                    {referencesVerified && (
                      <span className="inline-flex items-center rounded-full border border-stone-300 bg-stone-50 px-3 py-1 text-[11px] font-sans tracking-wide text-stone-600">
                        {t('know.referencesVerified', { defaultValue: 'References verified' })}
                      </span>
                    )}
                  </div>
                )}
              </header>




              {collapsed && (
                <div className="mb-14 border-y border-stone-200/70 py-6 text-center">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 font-sans mb-3">
                    {t('know.collapsedNotice', { defaultValue: 'Entry collapsed' })}
                  </p>
                  <button
                    type="button"
                    onClick={toggleCollapse}
                    className="inline-flex items-center gap-2 text-sm font-serif italic text-stone-700 hover:text-[hsl(var(--primary))] transition-colors"
                  >
                    <ChevronsUpDown className="w-4 h-4" />
                    {t('know.expandToRead', { defaultValue: 'Expand to read the full entry' })}
                  </button>
                </div>
              )}

              {!collapsed && <>

              {/* Mechanism */}
              {mechanism && (
                <section id="mechanism" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.mechanism', { defaultValue: 'Mechanism' })}
                  </h2>
                  <EditorialMarkdown>{mechanism}</EditorialMarkdown>
                </section>
              )}

              {/* Procedures */}
              {procedures && (
                <section id="procedures" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.procedures', { defaultValue: 'How it works' })}
                  </h2>
                  <EditorialMarkdown>{procedures}</EditorialMarkdown>
                </section>
              )}

              {/* Key concepts — glossary so clients recognise the vocabulary in session */}
              {coreConcepts.length > 0 && (
                <section id="concepts" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-2">
                    {t('know.concepts', { defaultValue: 'Key concepts' })}
                  </h2>
                  <p className="text-stone-500 text-sm font-sans mb-6">
                    {t('know.conceptsIntro', {
                      defaultValue: 'Terms you are likely to hear, in plain language.',
                    })}
                  </p>
                  <dl className="space-y-5">
                    {coreConcepts.map((c) => (
                      <div key={`${c.term}-${c.termEn}`} className="border-l-2 border-[hsl(var(--primary)/0.35)] pl-5">
                        <dt className="font-serif text-lg text-stone-900">
                          {c.term}
                          {c.termEn && c.termEn !== c.term && (
                            <span className="ml-2 text-xs font-sans uppercase tracking-[0.2em] text-stone-400">
                              {c.termEn}
                            </span>
                          )}
                        </dt>
                        <dd className="mt-1.5 text-stone-700 font-sans text-[15px] leading-[1.8]">{c.oneLiner}</dd>
                        {c.whyItMatters && (
                          <dd className="mt-1.5 text-stone-500 font-sans text-[14px] leading-[1.8]">
                            {c.whyItMatters}
                          </dd>
                        )}
                      </div>
                    ))}
                  </dl>
                </section>
              )}

              {/* First session — what actually happens when you walk in */}
              {firstSession && (
                <section id="firstSession" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.firstSession', { defaultValue: 'Your first session' })}
                  </h2>
                  <EditorialMarkdown>{firstSession}</EditorialMarkdown>
                </section>
              )}

              {/* Common misconceptions */}
              {misconceptions.length > 0 && (
                <section id="misconceptions" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.misconceptions', { defaultValue: 'Common misconceptions' })}
                  </h2>
                  <div className="space-y-4">
                    {misconceptions.map((m) => (
                      <div key={m.myth} className="border border-stone-200 rounded-sm bg-white/60 px-5 py-4">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-1.5">
                          {t('know.myth', { defaultValue: 'Often assumed' })}
                        </p>
                        <p className="font-serif text-[17px] text-stone-800 leading-relaxed mb-3">{m.myth}</p>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-1.5">
                          {t('know.reality', { defaultValue: 'In practice' })}
                        </p>
                        <p className="text-stone-700 font-sans text-[15px] leading-[1.8]">{m.reality}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}



              {/* Origin */}
              {origin && (
                <section id="origin" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.origin', { defaultValue: 'Origin' })}
                  </h2>
                  {therapy.origin_century && (
                    <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-3">
                      {therapy.origin_century}
                    </p>
                  )}
                  <EditorialMarkdown>{origin}</EditorialMarkdown>
                </section>
              )}

              {/* Who it helps */}
              {(population || hasDomainFlags(therapy) || relatedStages.length > 0 || ageGroupLabels.length > 0) && (
                <section id="helps" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.helps', { defaultValue: 'Who it helps' })}
                  </h2>
                  {population && (
                    <div className="mb-6">
                      <EditorialMarkdown>{population}</EditorialMarkdown>
                    </div>
                  )}
                  {targetDomainLabel && (
                    <p className="text-stone-700 leading-[1.85] text-base md:text-[17px] font-sans mb-6">
                      {targetDomainLabel}
                    </p>
                  )}
                  {(ageGroupLabels.length > 0 || ageRangeLabel) && (
                    <div className="mb-6">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-3">
                        {t('know.ageSuitability', { defaultValue: 'Age suitability' })}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {ageRangeLabel && (
                          <span className="inline-flex items-center px-3 py-1 text-xs font-sans bg-[hsl(var(--primary)/0.12)] text-stone-800 rounded-sm">
                            {ageRangeLabel}
                          </span>
                        )}
                        {ageGroupLabels.map((label) => (
                          <span key={label} className="inline-flex items-center px-3 py-1 text-xs font-sans border border-[hsl(var(--primary)/0.3)] text-stone-700 rounded-sm">
                            {label}
                          </span>
                        ))}
                        {therapy.requires_guardian_consent && (
                          <span className="inline-flex items-center px-3 py-1 text-xs font-sans border border-amber-300 bg-amber-50 text-amber-800 rounded-sm">
                            {t('know.guardianConsent', { defaultValue: 'Guardian consent required for minors' })}
                          </span>
                        )}
                      </div>
                      {ageNotes && (
                        <p className="text-stone-600 leading-[1.85] text-sm md:text-[15px] font-sans">
                          {ageNotes}
                        </p>
                      )}
                    </div>
                  )}
                  {relatedStages.length > 0 && (

                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-3">
                        {t('know.appliesAcross', { defaultValue: 'Applies across stages' })}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {relatedStages.map((n) => (
                          <span key={n} className="inline-flex items-center px-3 py-1 text-xs font-sans border border-[hsl(var(--primary)/0.3)] text-stone-700 rounded-sm">
                            {t(`stages.${n}.title`)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}




              {/* Cautions */}
              {(contraindications || cautionFlags.length > 0) && (
                <section id="cautions" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.cautions', { defaultValue: 'Cautions & contraindications' })}
                  </h2>
                  {contraindications && <EditorialMarkdown>{contraindications}</EditorialMarkdown>}
                  {cautionFlags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {cautionFlags.map((flag) => (
                        <span key={flag} className="inline-flex items-center px-3 py-1 text-xs font-sans border border-stone-300 bg-stone-50 text-stone-600 rounded-sm">
                          {flag}
                        </span>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* Session structure */}
              {hasStructure && (
                <section id="structure" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.structure', { defaultValue: 'Session structure' })}
                  </h2>
                  <dl className="grid grid-cols-2 md:grid-cols-3 gap-8 max-w-2xl">
                    {sessionLength && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                          {t('therapy.duration')}
                        </dt>
                        <dd className="font-serif text-2xl text-stone-800">
                          {sessionLength} <span className="text-sm text-stone-500">{t('therapy.minutes')}</span>
                        </dd>
                      </div>
                    )}
                    {sessionCount && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                          {t('therapy.sessions')}
                        </dt>
                        <dd className="font-serif text-2xl text-stone-800">
                          {sessionCount} <span className="text-sm text-stone-500">{t('therapy.sessionsLabel')}</span>
                        </dd>
                      </div>
                    )}
                    {frequencyLabel && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                          {t('know.frequency', { defaultValue: 'Frequency' })}
                        </dt>
                        <dd className="font-serif text-xl text-stone-800">{frequencyLabel}</dd>
                      </div>
                    )}
                    {formatLabel && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                          {t('know.format', { defaultValue: 'Format' })}
                        </dt>
                        <dd className="font-serif text-xl text-stone-800">{formatLabel}</dd>
                      </div>
                    )}
                    {(therapy.cost_per_session_min || therapy.cost_per_session_max) && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                          {t('know.typicalCost', { defaultValue: 'Typical cost (USD)' })}
                        </dt>
                        <dd className="font-serif text-2xl text-stone-800">
                          {range(therapy.cost_per_session_min, therapy.cost_per_session_max)}
                        </dd>
                      </div>
                    )}
                    {therapy.chata_stage && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                          {t('know.chataStage', { defaultValue: 'CHATA stage' })}
                        </dt>
                        <dd className="font-serif text-xl text-stone-800">{therapy.chata_stage}</dd>
                      </div>
                    )}
                  </dl>
                </section>
              )}

              {/* Practitioner guidance */}
              {guidance && (
                <section id="guidance" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.guidance', { defaultValue: 'Practitioner guidance' })}
                  </h2>
                  <EditorialMarkdown>{guidance}</EditorialMarkdown>
                </section>
              )}

              {/* Lineage & certifications */}
              {(certifications || organizations || lineageGroups.length > 0) && (
                <section id="lineage" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.lineage', { defaultValue: 'Lineage & certifications' })}
                  </h2>
                  {certifications && (
                    <div className="mb-6">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                        {t('therapy.certifications')}
                      </p>
                      <EditorialMarkdown size="sm">{certifications}</EditorialMarkdown>
                    </div>
                  )}
                  {organizations && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                        {t('therapy.organizations')}
                      </p>
                      <EditorialMarkdown size="sm">{organizations}</EditorialMarkdown>
                    </div>
                  )}
                  {lineageGroups.length > 0 && (
                    <div className="mt-6 space-y-4">
                      {lineageGroups.map((g) => (
                        <div key={g.key}>
                          <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                            {t(`know.${g.key}`, { defaultValue: g.fallback })}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {g.items!.map((item) => (
                              <span key={item} className="inline-flex items-center px-3 py-1 text-xs font-sans border border-[hsl(var(--primary)/0.3)] text-stone-700 rounded-sm">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Official body links live in "Further reading" to avoid duplication */}
                </section>
              )}

              {/* Related modalities */}
              {related.length > 0 && (
                <section id="related" className="mb-14 scroll-mt-24">
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                    {t('know.related', { defaultValue: 'Related modalities' })}
                  </h2>
                  <ul className="divide-y divide-stone-200/70 border-y border-stone-200/70">
                    {related.map((r) => {
                      const inner = (
                        <>
                          <div className="flex items-baseline justify-between gap-4">
                            <span className="font-serif italic text-lg text-stone-800 group-hover:text-[hsl(var(--primary))] transition-colors">
                              {r.name}
                            </span>
                            <span className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sans inline-flex items-center">
                              {r.relation
                                ? t(`know.${r.relation.replace(/_(.)/g, (_m, c) => c.toUpperCase())}`, {
                                    defaultValue: r.relation.replace(/_/g, ' '),
                                  })
                                : t('readEntry', { defaultValue: 'Read entry' })}
                              {r.linkId && (
                                <ArrowUpRight className="w-3 h-3 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                              )}
                            </span>
                          </div>
                          {r.difference && (
                            <p className="mt-2 text-sm leading-relaxed text-stone-600 font-sans max-w-2xl">
                              {r.difference}
                            </p>
                          )}
                        </>
                      );
                      return (
                        <li key={r.id}>
                          {r.linkId ? (
                            <Link to={`/framework/${r.linkId}`} className="block py-4 group">
                              {inner}
                            </Link>
                          ) : (
                            <div className="py-4">{inner}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                </section>
              )}

              {/* Further reading */}
              <section id="references" className="mb-14 scroll-mt-24">
                <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-5">
                  {t('know.furtherReading', { defaultValue: 'Further reading' })}
                </h2>

                {curatedReferences.length > 0 && (
                  <div className="mb-8">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-3">
                      {t('know.citations', { defaultValue: 'Citations' })}
                    </p>
                    <ul className="space-y-2">
                      {curatedReferences.map((r, i) => (
                        <li key={i} className="text-sm text-stone-700 font-sans">
                          {r.url ? (
                            <a href={r.url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center hover:text-[hsl(var(--primary))]">
                              {r.title}
                              <ExternalLink className="w-3 h-3 ml-1.5" />
                            </a>
                          ) : (
                            r.title
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {orgUrls.length > 0 && (
                  <div className="mb-8">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-3">
                      {t('know.officialBodies', { defaultValue: 'Official bodies & accreditation' })}
                    </p>
                    <ul className="space-y-2">
                      {orgUrls.map((o, i) => (
                        <li key={i}>
                          <a href={o.url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-amber-700 hover:text-amber-900 font-sans">
                            {o.name}
                            <ExternalLink className="w-3 h-3 ml-1.5" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sans mb-2">
                    {t('know.searchThisModality', { defaultValue: 'Search this modality' })}
                  </p>
                  <p className="text-sm text-stone-500 mb-4 font-sans">
                    {t('know.searchNote', { defaultValue: 'These are external search shortcuts, not citations curated by Resonow. Resonow does not endorse third-party content.' })}
                  </p>
                  <ul className="space-y-3">
                    {searchLinks.map((r) => (
                      <li key={r.label}>
                        <a href={r.url} target="_blank" rel="noopener noreferrer"
                          className="group inline-flex items-center text-stone-700 hover:text-[hsl(var(--primary))] font-sans">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mr-3 group-hover:text-stone-500">
                            {t('know.search', { defaultValue: 'Search' })}
                          </span>
                          <span>{r.label}</span>
                          <ExternalLink className="w-3.5 h-3.5 ml-2" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Find a practitioner — soft footer */}
              <section id="practitioners" className="mb-8 scroll-mt-24 border-t border-stone-200/70 pt-8">
                <p className="text-sm text-stone-600 font-sans mb-3">
                  {practitionerCount > 0
                    ? t('stats.practitionersAvailable', { count: practitionerCount })
                    : t('stats.noPractitioners')}
                </p>
                {practitionerCount > 0 && (
                  <Link
                    to={`/book-an-appointment?modalities=${therapy.id}`}
                    className="inline-flex items-center text-[11px] font-semibold tracking-[0.2em] uppercase text-stone-900 hover:text-[hsl(var(--primary))] transition-colors font-sans"
                  >
                    {t('know.findPractitioner', { defaultValue: 'Find a practitioner' })}
                    <ArrowUpRight className="w-3.5 h-3.5 ml-2" />
                  </Link>
                )}
              </section>

              <p className="mt-10 border-t border-stone-200/70 pt-5 text-[11px] leading-relaxed text-stone-400 font-sans">
                {t('know.copyright', {
                  defaultValue:
                    '© Resonow. Curated knowledge base. Personal, non-commercial citation with a link back is welcome; systematic scraping, mirroring or use for training third-party models is not permitted.',
                })}
              </p>
              </>}

            </article>

            {/* Right meta column */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-6 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 font-sans mb-2">
                    {t('know.metadata', { defaultValue: 'Entry metadata' })}
                  </p>
                  <dl className="space-y-3 font-sans">
                    {streamTypeLabel && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-stone-400">{t('filters.streamType', { defaultValue: 'Stream' })}</dt>
                        <dd className="text-stone-700">{streamTypeLabel}</dd>
                      </div>
                    )}
                    {typeLabel && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-stone-400">{t('know.type', { defaultValue: 'Type' })}</dt>
                        <dd className="text-stone-700">{typeLabel}</dd>
                      </div>
                    )}
                    {parentCategoryLabel && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-stone-400">{t('know.category', { defaultValue: 'Category' })}</dt>
                        <dd className="text-stone-700">{parentCategoryLabel}</dd>
                      </div>
                    )}
                    {evidenceLabel && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-stone-400">{t('filters.evidenceLevel', { defaultValue: 'Evidence' })}</dt>
                        <dd className="text-stone-700">{evidenceLabel}</dd>
                      </div>
                    )}
                    {targetDomainLabel && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-stone-400">{t('know.domain', { defaultValue: 'Domain' })}</dt>
                        <dd className="text-stone-700">{targetDomainLabel}</dd>
                      </div>
                    )}
                    {relatedStages.length > 0 && (
                      <div>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-stone-400">{t('know.stages', { defaultValue: 'Stages' })}</dt>
                        <dd className="text-stone-700">{relatedStages.join(' · ')}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TherapyKnowledgePage;
